import { useEffect, useRef, useState } from "react";
import { Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Inline Calendly booking widget.
 *
 * - Loads Calendly's widget.js on demand (once per page) and mounts an inline
 *   embed pointed at `url`. No API keys, no backend — the visitor books
 *   directly against your Calendly account through Calendly's own UI.
 * - Respects `prefers-reduced-motion` and provides a graceful fallback if the
 *   script fails to load or the URL is not configured yet.
 * - The Calendly URL is intentionally NOT hardcoded. Set
 *   `VITE_FSAP_DISCOVERY_URL` (or pass `url` directly) so the URL can be
 *   rotated without a code change.
 *
 * Docs: https://help.calendly.com/hc/en-us/articles/223147027
 */

type Props = {
  /** Full public Calendly event URL, e.g. https://calendly.com/drsullyspeaks/fsap-discovery */
  url?: string;
  /** Optional prefill values passed to Calendly. */
  prefill?: { name?: string; email?: string };
  /** Optional UTM tags for attribution. */
  utm?: { utmSource?: string; utmMedium?: string; utmCampaign?: string };
  /** Minimum height for the inline embed (defaults to 720px). */
  minHeight?: number;
  /** Fallback CTA target used when the URL is not configured. */
  fallbackHref?: string;
};

const CALENDLY_SCRIPT_SRC = "https://assets.calendly.com/assets/external/widget.js";
const CALENDLY_STYLES_HREF = "https://assets.calendly.com/assets/external/widget.css";

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (opts: {
        url: string;
        parentElement: HTMLElement;
        prefill?: Record<string, unknown>;
        utm?: Record<string, unknown>;
      }) => void;
    };
  }
}

/** Load Calendly's script+styles exactly once per page. */
function ensureCalendlyLoaded(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.Calendly) return Promise.resolve();

  return new Promise((resolve, reject) => {
    // Styles
    if (!document.querySelector(`link[href="${CALENDLY_STYLES_HREF}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = CALENDLY_STYLES_HREF;
      document.head.appendChild(link);
    }
    // Script
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${CALENDLY_SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("calendly-load-failed")), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = CALENDLY_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("calendly-load-failed"));
    document.body.appendChild(script);
  });
}

export const CalendlyInlineWidget = ({
  url,
  prefill,
  utm,
  minHeight = 720,
  // Must be a real route — /strategy-session does not exist and 404s.
  fallbackHref = "/inquire",
}: Props) => {
  const mountRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "failed">(
    "idle",
  );

  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setStatus("loading");

    ensureCalendlyLoaded()
      .then(() => {
        if (cancelled || !mountRef.current || !window.Calendly) return;
        // Clear any previous embed (HMR / prop changes) to prevent duplicates.
        mountRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url,
          parentElement: mountRef.current,
          prefill: prefill as Record<string, unknown> | undefined,
          utm: utm as Record<string, unknown> | undefined,
        });
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [url, prefill, utm]);

  // Missing URL — clear operator-facing setup state, visitor-facing fallback.
  if (!url) {
    return (
      <div className="border border-dashed border-border bg-navy-deep/40 p-8 text-center">
        <Calendar className="h-6 w-6 text-gold mx-auto mb-4" aria-hidden />
        <p className="text-silver text-sm leading-relaxed max-w-xl mx-auto">
          The FSAP discovery scheduler is coming online. In the meantime,
          request a consultation and we'll respond within one business day.
        </p>
        <Link
          to={fallbackHref}
          className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.22em] text-gold hover:text-gold-bright"
        >
          Request consultation
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    );
  }

  // Failed to load Calendly (blocked, offline, ad-blocker) — deep link out.
  if (status === "failed") {
    return (
      <div className="border border-border bg-navy-deep/40 p-8 text-center">
        <Calendar className="h-6 w-6 text-gold mx-auto mb-4" aria-hidden />
        <p className="text-silver text-sm leading-relaxed max-w-xl mx-auto">
          The scheduler couldn't load in your browser. Open the booking page
          directly to pick a time.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.22em] text-gold hover:text-gold-bright"
        >
          Open scheduler
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </a>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Skeleton while Calendly boots — prevents CLS on the inline embed. */}
      {status !== "ready" && (
        <div
          aria-hidden
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-navy-elevated via-navy to-navy-deep"
          style={{ minHeight }}
        />
      )}
      <div
        ref={mountRef}
        aria-label="Book a discovery consultation"
        className="calendly-inline-widget"
        style={{ minWidth: 320, height: minHeight }}
      />
    </div>
  );
};
